import type { HydraMarket, HydraOrderbook } from '@/clients/HydraClient';
import type { TriggerRun, TriggerRunStatus } from './types';

export const G9_SCALP_KIND = 'g9.scalp';

export type G9EntryMode = 'price_trigger' | 'resting_limit' | 'breakout';

export interface G9ScalpConfig {
  ticker: string;
  sport: string;
  matchupLabel: string;
  outcomeLabel: string;
  side: 'yes' | 'no';
  quantity: number;
  entryPriceCents: number;
  targetPriceCents: number;
  /** Optional protective floor below the entry; once filled, the position
   *  exits at market if the bid crosses this. null = target-only (no stop). */
  stopPriceCents: number | null;
  entryMode: G9EntryMode;
  cancelAtMs: number | null;
}

export interface G9ScalpState {
  initialPositionCount: number;
  entryFilledCount: number;
  exitQuantity: number | null;
  entryOrderId: string | null;
  exitOrderId: string | null;
  entryPlacedAtMs: number | null;
  exitPlacedAtMs: number | null;
  entryFilledAtMs: number | null;
  exitFilledAtMs: number | null;
  lastBestBidCents: number | null;
  lastBestAskCents: number | null;
}

export interface G9ScalpSnapshot {
  nowMs: number;
  bestBidCents: number | null;
  bestAskCents: number | null;
  currentPositionCount: number;
  entryFilledCount: number;
  exitFilledCount: number;
  entryOrderOpen: boolean;
  exitOrderOpen: boolean;
}

export type G9ScalpRun = TriggerRun<G9ScalpConfig, G9ScalpState>;

export type G9Decision =
  | { type: 'place_entry'; message: string }
  | { type: 'place_exit'; message: string; cancelOpenEntry?: boolean }
  | { type: 'cancel'; message: string }
  | { type: 'complete'; message: string }
  | { type: 'fail'; message: string };

export function createG9ScalpState(initialPositionCount: number): G9ScalpState {
  return {
    initialPositionCount,
    entryFilledCount: 0,
    exitQuantity: null,
    entryOrderId: null,
    exitOrderId: null,
    entryPlacedAtMs: null,
    exitPlacedAtMs: null,
    entryFilledAtMs: null,
    exitFilledAtMs: null,
    lastBestBidCents: null,
    lastBestAskCents: null,
  };
}

export function isG9ScalpRun(run: TriggerRun): run is G9ScalpRun {
  return run.kind === G9_SCALP_KIND;
}

export function isTerminalStatus(status: TriggerRunStatus): boolean {
  return (
    status === 'completed' ||
    status === 'canceled' ||
    status === 'failed' ||
    // A wound-down run is finished: its old-org orders are confirmed gone. The
    // other three wind-down states still need a manager, so they stay active.
    status === 'wound_down'
  );
}

/**
 * Whether this run is held for organization recovery (#287 pass 2).
 *
 * Keyed on the RECOVERY SNAPSHOT, not on the status, because the one case that
 * matters most does not show up in the status at all: a run whose original
 * organization cannot be established is stored as terminal `failed`, and a
 * status-only test would let the generic terminal path hide it in the closed list
 * and offer a Clear the server would refuse.
 *
 * `complete` is excluded: a finished recovery is an ordinary terminal run.
 */
export function isRecoveryHeld(run: TriggerRun): boolean {
  const state = run.recovery?.state;
  return state != null && state !== 'complete';
}

/**
 * The owner's view of terminal. A held run is never terminal to the owner however
 * its status reads, so it stays visible and keeps its Clear button hidden.
 */
export function isOwnerTerminal(run: TriggerRun): boolean {
  return isTerminalStatus(run.status) && !isRecoveryHeld(run);
}

/**
 * Whether the OWNER may act on this run at all.
 *
 * False for every held run. The owner has no cancel, edit, resume, recover, or
 * clear path once a run is in recovery — the server refuses all of them, and a
 * button that only produces a 409 is worse than no button.
 */
export function ownerCanCancel(run: TriggerRun): boolean {
  return !isRecoveryHeld(run) && !isTerminalStatus(run.status);
}

/**
 * Fixed copy for one recovery outcome.
 *
 * Lives here, shared by the owner's card and the manager panel, because the two
 * surfaces describe the SAME durable fact to different audiences and must never
 * disagree about what happened to an order. The server sends only a stable code;
 * all wording is decided on this side.
 */
export function recoveryOutcomeLabel(outcome: {
  result: 'canceled' | 'already-gone' | 'failed';
  errorCode?: string;
}): string {
  if (outcome.result === 'canceled') return 'Cancelled';
  if (outcome.result === 'already-gone') return 'Already gone';
  switch (outcome.errorCode) {
    case 'already_gone_unproven':
      return 'Not found, unconfirmed';
    case 'audit_write_failed':
      return 'Audit not recorded';
    default:
      return 'Cancel refused';
  }
}

/** True when no manager route exists either, so a human must reconcile by hand. */
export function isManualOnlyRecovery(recovery: { reasons: Array<{ code: string }> }): boolean {
  return recovery.reasons.some(
    (reason) =>
      reason.code === 'original_org_unverifiable' || reason.code === 'original_trader_missing',
  );
}

function maxPrice(levels: Array<{ price: number }> | undefined): number | null {
  if (!levels || levels.length === 0) return null;
  let best: number | null = null;
  for (const level of levels) {
    if (!Number.isFinite(level.price)) continue;
    best = best == null ? level.price : Math.max(best, level.price);
  }
  return best;
}

export function bestBidCents(book: HydraOrderbook | null, side: 'yes' | 'no'): number | null {
  if (!book) return null;
  return maxPrice(side === 'yes' ? book.yes : book.no);
}

export function bestAskCents(book: HydraOrderbook | null, side: 'yes' | 'no'): number | null {
  if (!book) return null;
  const oppositeBestBid = maxPrice(side === 'yes' ? book.no : book.yes);
  return oppositeBestBid == null ? null : 100 - oppositeBestBid;
}

export function marketBidCents(market: HydraMarket | null, side: 'yes' | 'no'): number | null {
  if (!market) return null;
  if (side === 'yes') return market.yesBid ?? (market.noAsk == null ? null : 100 - market.noAsk);
  return market.noBid ?? (market.yesAsk == null ? null : 100 - market.yesAsk);
}

export function marketAskCents(market: HydraMarket | null, side: 'yes' | 'no'): number | null {
  if (!market) return null;
  if (side === 'yes') return market.yesAsk ?? (market.noBid == null ? null : 100 - market.noBid);
  return market.noAsk ?? (market.yesBid == null ? null : 100 - market.yesBid);
}

export function effectiveBidCents(
  book: HydraOrderbook | null,
  market: HydraMarket | null,
  side: 'yes' | 'no',
): number | null {
  return bestBidCents(book, side) ?? marketBidCents(market, side);
}

export function effectiveAskCents(
  book: HydraOrderbook | null,
  market: HydraMarket | null,
  side: 'yes' | 'no',
): number | null {
  return bestAskCents(book, side) ?? marketAskCents(market, side);
}

export function evaluateG9ScalpRun(run: G9ScalpRun, snapshot: G9ScalpSnapshot): G9Decision | null {
  const { config, state } = run;
  const positionDelta = Math.max(0, snapshot.currentPositionCount - state.initialPositionCount);
  const observedEntryFilledCount = Math.floor(
    Math.max(snapshot.entryFilledCount, positionDelta, state.entryFilledCount),
  );
  const entryOrderClosed =
    state.entryOrderId != null && !snapshot.entryOrderOpen && state.entryPlacedAtMs != null;
  const hasFullEntryFill = observedEntryFilledCount >= config.quantity;
  const hasClosedPartialEntryFill = entryOrderClosed && observedEntryFilledCount > 0;
  const exitQuantity = state.exitQuantity ?? config.quantity;
  const hasExitFill =
    snapshot.exitFilledCount >= exitQuantity ||
    (
      state.exitOrderId != null &&
      !snapshot.exitOrderOpen &&
      state.exitPlacedAtMs != null &&
      snapshot.currentPositionCount <= state.initialPositionCount
    );
  const autoCancelDue =
    config.cancelAtMs != null &&
    snapshot.nowMs >= config.cancelAtMs &&
    (run.status === 'armed' || run.status === 'working_entry');

  if (run.status === 'armed' && autoCancelDue) {
    return { type: 'cancel', message: 'Auto-cancel time reached' };
  }

  if (run.status === 'armed') {
    if (config.entryMode === 'resting_limit') {
      return { type: 'place_entry', message: `Placing resting entry at ${config.entryPriceCents}c` };
    }
    if (snapshot.bestAskCents == null) return null;
    if (snapshot.bestAskCents <= config.entryPriceCents) {
      return {
        type: 'place_entry',
        message: `Entry trigger touched: ask ${snapshot.bestAskCents}c <= ${config.entryPriceCents}c`,
      };
    }
    return null;
  }

  if (run.status === 'working_entry') {
    if (hasFullEntryFill || hasClosedPartialEntryFill || (autoCancelDue && observedEntryFilledCount > 0)) {
      const partialTimedOut = autoCancelDue && !hasFullEntryFill && snapshot.entryOrderOpen;
      return {
        type: 'place_exit',
        message: partialTimedOut
          ? `Auto-cancel reached after ${observedEntryFilledCount} filled; placing target sell at ${config.targetPriceCents}c`
          : `Entry filled; placing target sell at ${config.targetPriceCents}c`,
        cancelOpenEntry: partialTimedOut,
      };
    }
    if (entryOrderClosed) {
      return { type: 'fail', message: 'Entry order is no longer open and no entry fill was detected' };
    }
    if (autoCancelDue) {
      return { type: 'cancel', message: 'Auto-cancel time reached' };
    }
    return null;
  }

  if (run.status === 'working_exit') {
    if (hasExitFill) {
      return { type: 'complete', message: 'Exit fill detected; run complete' };
    }
    if (state.exitOrderId && !snapshot.exitOrderOpen && state.exitPlacedAtMs != null) {
      return { type: 'fail', message: 'Exit order is no longer open and no exit fill was detected' };
    }
  }

  return null;
}
